import {ReactNode} from "react";

export type OtsSegment<T extends string> = {
  id: T;
  label: ReactNode;
  /** Optional tally shown beside the label, e.g. how many rows the segment would show. */
  count?: number;
  disabled?: boolean;
};

export type OtsSegmentedControlProps<T extends string> = {
  segments: OtsSegment<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  size?: "compact" | "default";
  className?: string;
};

/** A single-choice switch between a few views of the same content. */
export const OtsSegmentedControl = <T extends string>({
  segments,
  value,
  onChange,
  ariaLabel,
  size = "default",
  className,
}: OtsSegmentedControlProps<T>) => <div
  role="group"
  aria-label={ariaLabel}
  className={["inline-flex rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface-variant)] p-0.5", className].filter(Boolean).join(" ")}>
  {segments.map(segment => {
    const selected = segment.id === value;
    return <button
      key={segment.id}
      type="button"
      aria-pressed={selected}
      disabled={segment.disabled}
      onClick={() => onChange(segment.id)}
      className={[
        "inline-flex items-center gap-1.5 rounded-md font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)] disabled:cursor-not-allowed disabled:opacity-50",
        size === "compact" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
        selected
          ? "bg-[var(--ots-surface)] text-[var(--ots-primary)] shadow-sm"
          : "text-[var(--ots-text-muted)] hover:text-[var(--ots-text)]",
      ].join(" ")}>
      {segment.label}
      {segment.count != null && <span
        className={[
          "rounded-full px-1.5 text-[11px] tabular-nums",
          selected ? "bg-[var(--ots-primary-container)]" : "bg-[var(--ots-neutral-container)]",
        ].join(" ")}>
        {segment.count}
      </span>}
    </button>;
  })}
</div>;
